import axios from 'axios';
import { API_URL } from '@/lib/api';

/**
 * Web Push (VAPID) — subscribe / unsubscribe helpers.
 *
 * Flow:
 *  1. Ensure a service worker is registered (or use the one created by CRA).
 *  2. Request Notification.permission if not yet granted.
 *  3. registration.pushManager.subscribe({ userVisibleOnly, applicationServerKey })
 *  4. POST subscription JSON to /api/push/subscribe.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getRegistration() {
  if (!('serviceWorker' in navigator)) throw new Error('Service worker not supported');
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    reg = await navigator.serviceWorker.register('/sw.js');
  }
  await navigator.serviceWorker.ready;
  return reg;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getVapidPublicKey() {
  const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (envKey) return envKey;
  const res = await axios.get(`${API_URL}/push/vapid-public-key`);
  return res.data?.data?.public_key || '';
}

/**
 * Ensures the browser is subscribed and posts the subscription to the backend.
 * Returns { subscribed: true, subscription } or { subscribed: false, reason }.
 */
export async function enablePush() {
  if (!isPushSupported()) return { subscribed: false, reason: 'unsupported' };

  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') return { subscribed: false, reason: perm };

  const key = await getVapidPublicKey();
  if (!key) return { subscribed: false, reason: 'no-vapid-key' };

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }
  // Send to backend
  await axios.post(`${API_URL}/push/subscribe`, sub.toJSON(), {
    headers: { 'Content-Type': 'application/json' },
  });
  return { subscribed: true, subscription: sub };
}

export async function disablePush() {
  if (!isPushSupported()) return { unsubscribed: false };
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await axios.post(`${API_URL}/push/unsubscribe`, { endpoint: sub.endpoint });
    } catch { /* server may be down; unsub locally anyway */ }
    await sub.unsubscribe();
  }
  return { unsubscribed: true };
}

export async function currentPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission: Notification.permission,
    subscribed: !!sub,
  };
}
